import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Typography } from "@material-ui/core";
import { BounceLoader } from "react-spinners";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { publicRequest } from "../../api/requestMethods";
import Layout from "../../components/Global/Layout";
import logo from "../../assets/img/logo.png";

// ──────────────────────────────────────────────
// Forgot Password Page (3-step OTP flow)
// Step 1: Enter email → sends OTP
// Step 2: Enter 6-digit OTP code
// Step 3: Set new password
// ──────────────────────────────────────────────

const RESEND_COOLDOWN = 30; // seconds

const ForgotPasswordPage = () => {
    // ── Step state ──────────────────────────────
    const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password
    const [loading, setLoading] = useState(false);

    // ── Form values ─────────────────────────────
    const [email, setEmail] = useState("");
    const [maskedEmail, setMaskedEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // ── UI state ────────────────────────────────
    const [error, setError] = useState("");
    const [focusedInput, setFocusedInput] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [success, setSuccess] = useState(false);

    // ── Resend cooldown ─────────────────────────
    const [resendCooldown, setResendCooldown] = useState(0);
    const cooldownRef = useRef(null);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, []);

    const startResendCooldown = useCallback(() => {
        setResendCooldown(RESEND_COOLDOWN);
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    cooldownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    // ── Step 1: Send OTP ────────────────────────
    const handleSendCode = async (e) => {
        e && e.preventDefault();
        if (!email.trim()) {
            setError("Please enter your email address");
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError("Please enter a valid email address");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await publicRequest.post(
                "/user/forgot-password/send-otp",
                { email: email.trim().toLowerCase() }
            );

            if (response.data) {
                setMaskedEmail(response.data.maskedEmail || email);
                setStep(2);
                startResendCooldown();
                toast.success("Verification code sent to your email!");
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Failed to send verification code. Please try again.";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Verify OTP → advance to Step 3 ─
    const handleVerifyCode = (e) => {
        e && e.preventDefault();
        if (!code || code.length !== 6) {
            setError("Please enter the 6-digit verification code");
            return;
        }
        setError("");
        setStep(3);
    };

    // ── Step 3: Reset password ──────────────────
    const handleResetPassword = async (e) => {
        e && e.preventDefault();

        if (!newPassword) {
            setError("Please enter a new password");
            return;
        }
        if (newPassword.length < 4) {
            setError("Password must be at least 4 characters");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await publicRequest.post(
                "/user/forgot-password/reset",
                {
                    email: email.trim().toLowerCase(),
                    code,
                    newPassword,
                }
            );

            if (response.data) {
                setSuccess(true);
                toast.success("Password reset successfully!");
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Failed to reset password. Please try again.";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // ── Resend OTP ──────────────────────────────
    const handleResendCode = async () => {
        if (resendCooldown > 0) return;
        setLoading(true);
        setError("");
        try {
            await publicRequest.post("/user/forgot-password/send-otp", {
                email: email.trim().toLowerCase(),
            });
            startResendCooldown();
            setCode("");
            toast.success("New verification code sent!");
        } catch (err) {
            const message =
                err.response?.data?.message || "Failed to resend code.";
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // ── Step indicator ──────────────────────────
    const StepIndicator = () => (
        <div style={styles.stepContainer}>
            {[1, 2, 3].map((s) => (
                <div key={s} style={styles.stepRow}>
                    <div
                        style={{
                            ...styles.stepCircle,
                            backgroundColor: step >= s ? "#017109" : "#e0e0e0",
                            boxShadow:
                                step === s ? "0 0 0 4px rgba(1, 113, 9, 0.15)" : "none",
                        }}
                    >
                        {step > s ? (
                            <CheckCircleOutlineIcon
                                style={{ fontSize: 16, color: "#fff" }}
                            />
                        ) : (
                            <span style={styles.stepNumber}>{s}</span>
                        )}
                    </div>
                    <span
                        style={{
                            ...styles.stepLabel,
                            color: step >= s ? "#2c3e50" : "#95a5a6",
                            fontWeight: step === s ? 600 : 400,
                        }}
                    >
                        {s === 1 ? "Email" : s === 2 ? "Verify" : "Reset"}
                    </span>
                    {s < 3 && (
                        <div
                            style={{
                                ...styles.stepLine,
                                backgroundColor: step > s ? "#017109" : "#e0e0e0",
                            }}
                        />
                    )}
                </div>
            ))}
        </div>
    );

    // ── Success state ───────────────────────────
    if (success) {
        return (
            <Layout>
                <div style={styles.pageContainer}>
                    <div style={styles.card}>
                        <div style={styles.successContainer}>
                            <div style={styles.successIcon}>
                                <CheckCircleOutlineIcon
                                    style={{ fontSize: 56, color: "#017109" }}
                                />
                            </div>
                            <h2 style={styles.successTitle}>Password Reset Successful!</h2>
                            <p style={styles.successSubtitle}>
                                Your password has been updated. You can now log in with your
                                new password.
                            </p>
                            <Link to="/login" style={{ textDecoration: "none", width: "100%" }}>
                                <button type="button" style={styles.primaryButton}>
                                    Go to Login
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={styles.pageContainer}>
                <div style={styles.card}>
                    {/* Loading overlay */}
                    {loading && (
                        <div style={styles.loadingOverlay}>
                            <BounceLoader size={35} color={"#017109"} />
                        </div>
                    )}

                    {/* Logo */}
                    <img src={logo} alt="Lusuku" style={styles.logo} />

                    {/* Title */}
                    <h1 style={styles.title}>
                        {step === 1
                            ? "Forgot Password?"
                            : step === 2
                                ? "Verify Your Email"
                                : "Set New Password"}
                    </h1>
                    <p style={styles.subtitle}>
                        {step === 1
                            ? "Enter your email address and we'll send you a verification code"
                            : step === 2
                                ? `Enter the 6-digit code sent to ${maskedEmail}`
                                : "Choose a new password for your account"}
                    </p>

                    {/* Step Indicator */}
                    <StepIndicator />

                    {/* Error message */}
                    {error && (
                        <Typography
                            variant="body2"
                            style={styles.errorMessage}
                        >
                            {error}
                        </Typography>
                    )}

                    {/* ── STEP 1: Email ─────────────────── */}
                    {step === 1 && (
                        <form onSubmit={handleSendCode} style={styles.form}>
                            <div style={styles.inputWrapper}>
                                <EmailIcon
                                    style={{
                                        ...styles.inputIcon,
                                        color: focusedInput === "email" ? "#017109" : "#95a5a6",
                                    }}
                                />
                                <input
                                    type="email"
                                    placeholder="Enter email address..."
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError("");
                                    }}
                                    onFocus={() => setFocusedInput("email")}
                                    onBlur={() => setFocusedInput(null)}
                                    style={styles.input}
                                    autoFocus
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                style={{
                                    ...styles.primaryButton,
                                    opacity: loading || !email.trim() ? 0.6 : 1,
                                    cursor: loading || !email.trim() ? "not-allowed" : "pointer",
                                }}
                                disabled={loading || !email.trim()}
                            >
                                Send Verification Code
                            </button>
                            <div style={styles.backLinkContainer}>
                                <Link to="/login" style={styles.backLink}>
                                    <ArrowBackIcon style={{ fontSize: 16, marginRight: 4 }} />
                                    Back to Login
                                </Link>
                            </div>
                        </form>
                    )}

                    {/* ── STEP 2: OTP Code ──────────────── */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyCode} style={styles.form}>
                            <div style={styles.inputWrapper}>
                                <VpnKeyIcon
                                    style={{
                                        ...styles.inputIcon,
                                        color: focusedInput === "code" ? "#017109" : "#95a5a6",
                                    }}
                                />
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="Enter 6-digit code"
                                    value={code}
                                    onChange={(e) => {
                                        const cleaned = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 6);
                                        setCode(cleaned);
                                        setError("");
                                    }}
                                    onFocus={() => setFocusedInput("code")}
                                    onBlur={() => setFocusedInput(null)}
                                    style={{
                                        ...styles.input,
                                        letterSpacing: "8px",
                                        textAlign: "center",
                                        fontSize: "20px",
                                        fontWeight: 600,
                                        paddingLeft: "16px",
                                    }}
                                    maxLength={6}
                                    autoFocus
                                    autoComplete="one-time-code"
                                />
                            </div>

                            {/* Resend link */}
                            <div style={styles.resendContainer}>
                                {resendCooldown > 0 ? (
                                    <span style={styles.resendDisabled}>
                                        Resend code in {resendCooldown}s
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResendCode}
                                        style={styles.resendButton}
                                        disabled={loading}
                                    >
                                        Didn't receive the code? Resend
                                    </button>
                                )}
                            </div>

                            <button
                                type="submit"
                                style={{
                                    ...styles.primaryButton,
                                    opacity: loading || code.length !== 6 ? 0.6 : 1,
                                    cursor:
                                        loading || code.length !== 6 ? "not-allowed" : "pointer",
                                }}
                                disabled={loading || code.length !== 6}
                            >
                                Verify Code
                            </button>

                            <div style={styles.backLinkContainer}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep(1);
                                        setCode("");
                                        setError("");
                                    }}
                                    style={styles.backLink}
                                >
                                    <ArrowBackIcon style={{ fontSize: 16, marginRight: 4 }} />
                                    Change email
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── STEP 3: New Password ──────────── */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} style={styles.form}>
                            {/* New Password */}
                            <div style={styles.inputWrapper}>
                                <LockIcon
                                    style={{
                                        ...styles.inputIcon,
                                        color:
                                            focusedInput === "newPassword" ? "#017109" : "#95a5a6",
                                    }}
                                />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="New password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setError("");
                                    }}
                                    onFocus={() => setFocusedInput("newPassword")}
                                    onBlur={() => setFocusedInput(null)}
                                    style={{ ...styles.input, paddingRight: "48px" }}
                                    autoFocus
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={styles.eyeButton}
                                    tabIndex={-1}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <VisibilityOffIcon style={{ fontSize: 20 }} />
                                    ) : (
                                        <VisibilityIcon style={{ fontSize: 20 }} />
                                    )}
                                </button>
                            </div>

                            {/* Confirm Password */}
                            <div style={styles.inputWrapper}>
                                <LockIcon
                                    style={{
                                        ...styles.inputIcon,
                                        color:
                                            focusedInput === "confirmPassword"
                                                ? "#017109"
                                                : "#95a5a6",
                                    }}
                                />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setError("");
                                    }}
                                    onFocus={() => setFocusedInput("confirmPassword")}
                                    onBlur={() => setFocusedInput(null)}
                                    style={{ ...styles.input, paddingRight: "48px" }}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={styles.eyeButton}
                                    tabIndex={-1}
                                    aria-label={
                                        showConfirmPassword ? "Hide password" : "Show password"
                                    }
                                >
                                    {showConfirmPassword ? (
                                        <VisibilityOffIcon style={{ fontSize: 20 }} />
                                    ) : (
                                        <VisibilityIcon style={{ fontSize: 20 }} />
                                    )}
                                </button>
                            </div>

                            {/* Password requirements hint */}
                            <p style={styles.passwordHint}>
                                Password must be at least 4 characters long
                            </p>

                            <button
                                type="submit"
                                style={{
                                    ...styles.primaryButton,
                                    opacity:
                                        loading || !newPassword || !confirmPassword ? 0.6 : 1,
                                    cursor:
                                        loading || !newPassword || !confirmPassword
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                                disabled={loading || !newPassword || !confirmPassword}
                            >
                                Reset Password
                            </button>

                            <div style={styles.backLinkContainer}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep(2);
                                        setNewPassword("");
                                        setConfirmPassword("");
                                        setError("");
                                    }}
                                    style={styles.backLink}
                                >
                                    <ArrowBackIcon style={{ fontSize: 16, marginRight: 4 }} />
                                    Back to verification
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </Layout>
    );
};

// ── Styles ──────────────────────────────────────
const styles = {
    pageContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "85vh",
        backgroundColor: "#f5f7fa",
        padding: "20px",
    },
    card: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "420px",
        maxWidth: "100%",
        padding: "40px 32px",
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow:
            "0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
    },
    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10,
        borderRadius: "16px",
    },
    logo: {
        width: "80px",
        height: "80px",
        marginBottom: "20px",
        filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
    },
    title: {
        fontSize: "24px",
        fontWeight: 700,
        margin: "0 0 8px 0",
        fontFamily: "'Poppins', sans-serif",
        color: "#2c3e50",
        letterSpacing: "-0.5px",
        textAlign: "center",
    },
    subtitle: {
        fontSize: "14px",
        fontWeight: 400,
        margin: "0 0 24px 0",
        textAlign: "center",
        fontFamily: "'Inter', sans-serif",
        color: "#7f8c8d",
        lineHeight: "1.5",
        maxWidth: "320px",
    },
    // ── Step indicator ─────────────────────
    stepContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "24px",
        width: "100%",
        maxWidth: "280px",
    },
    stepRow: {
        display: "flex",
        alignItems: "center",
        flex: 1,
    },
    stepCircle: {
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        flexShrink: 0,
    },
    stepNumber: {
        fontSize: "12px",
        fontWeight: 700,
        color: "#fff",
        fontFamily: "'Inter', sans-serif",
    },
    stepLabel: {
        fontSize: "11px",
        fontFamily: "'Inter', sans-serif",
        marginLeft: "6px",
        whiteSpace: "nowrap",
    },
    stepLine: {
        flex: 1,
        height: "2px",
        margin: "0 8px",
        borderRadius: "1px",
        transition: "background-color 0.3s ease",
        minWidth: "20px",
    },
    // ── Form ───────────────────────────────
    form: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    inputWrapper: {
        position: "relative",
        width: "100%",
        marginBottom: "16px",
    },
    inputIcon: {
        position: "absolute",
        left: "16px",
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: "20px",
        pointerEvents: "none",
        zIndex: 1,
        transition: "color 0.3s ease",
    },
    input: {
        width: "100%",
        height: "48px",
        borderRadius: "8px",
        border: "2px solid #e0e0e0",
        padding: "0 16px 0 48px",
        backgroundColor: "#f8f9fa",
        fontSize: "15px",
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        color: "#2c3e50",
        transition: "all 0.3s ease",
        boxSizing: "border-box",
        outline: "none",
    },
    eyeButton: {
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#95a5a6",
        transition: "color 0.2s ease",
        zIndex: 1,
    },
    primaryButton: {
        width: "100%",
        backgroundColor: "#017109",
        color: "#fff",
        padding: "14px 24px",
        borderRadius: "8px",
        border: "none",
        fontSize: "16px",
        fontWeight: 600,
        fontFamily: "'Poppins', sans-serif",
        textTransform: "none",
        letterSpacing: "0.5px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 12px rgba(1, 113, 9, 0.2)",
        marginTop: "8px",
    },
    errorMessage: {
        color: "#e74c3c",
        fontSize: "13px",
        backgroundColor: "#fee",
        padding: "10px 12px",
        borderRadius: "6px",
        marginBottom: "16px",
        fontFamily: "'Inter', sans-serif",
        fontWeight: 500,
        width: "100%",
        textAlign: "center",
        boxSizing: "border-box",
    },
    // ── Resend ─────────────────────────────
    resendContainer: {
        textAlign: "center",
        marginBottom: "12px",
        width: "100%",
    },
    resendDisabled: {
        fontSize: "13px",
        color: "#95a5a6",
        fontFamily: "'Inter', sans-serif",
        fontStyle: "italic",
    },
    resendButton: {
        background: "none",
        border: "none",
        color: "#017109",
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif",
        cursor: "pointer",
        textDecoration: "underline",
        padding: 0,
    },
    // ── Back / navigation ──────────────────
    backLinkContainer: {
        marginTop: "20px",
        textAlign: "center",
        width: "100%",
    },
    backLink: {
        display: "inline-flex",
        alignItems: "center",
        background: "none",
        border: "none",
        textDecoration: "none",
        color: "#017109",
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif",
        cursor: "pointer",
        transition: "color 0.2s ease",
    },
    // ── Password hint ──────────────────────
    passwordHint: {
        fontSize: "12px",
        color: "#95a5a6",
        fontFamily: "'Inter', sans-serif",
        fontStyle: "italic",
        marginBottom: "12px",
        textAlign: "left",
        width: "100%",
        margin: "0 0 12px 0",
    },
    // ── Success ────────────────────────────
    successContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 0",
        width: "100%",
    },
    successIcon: {
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        backgroundColor: "rgba(1, 113, 9, 0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "24px",
    },
    successTitle: {
        fontSize: "22px",
        fontWeight: 700,
        fontFamily: "'Poppins', sans-serif",
        color: "#2c3e50",
        marginBottom: "8px",
        textAlign: "center",
    },
    successSubtitle: {
        fontSize: "14px",
        fontFamily: "'Inter', sans-serif",
        color: "#7f8c8d",
        textAlign: "center",
        marginBottom: "32px",
        lineHeight: "1.5",
    },
};

export default ForgotPasswordPage;
